/**
 * Dokumente, Vorlagen und E-Mail (Plan 06): Schlüssel, Schemas, DTOs und die Regeln aus Abschnitt 6
 * (Platzhalter-Kontext mit deutscher Formatierung, Vorlagen rendern, Pflichtwerte des Vertrags, E-Mail-Fälligkeit).
 * Die sechs E-Mail-Texte stammen aus dem Prototyp (screens-aktion.js MAILS) und sind als Startfassung markiert.
 */
import { z } from 'zod';
import { addDays, fmtDate } from './format.js';

/* ---------- Schlüssel und Enums ---------- */
export const DOCUMENT_TEMPLATE_KEYS = ['contract_owner', 'planting_confirmation', 'site_visit', 'monitoring_doc'] as const;
export type DocumentTemplateKey = (typeof DOCUMENT_TEMPLATE_KEYS)[number];
export const DOCUMENT_TEMPLATE_NAMES: Record<DocumentTemplateKey, string> = {
  contract_owner: 'Waldbesitzervertrag',
  planting_confirmation: 'Pflanzbestätigung',
  site_visit: 'Begehungsprotokoll',
  monitoring_doc: 'Monitoring-Dokumentation',
};

export const EMAIL_TEMPLATE_KEYS = ['after_site_visit', 'four_weeks_before', 'one_week_before', 'thanks_after', 'request_confirmation', 'monitoring_inquiry'] as const;
export type EmailTemplateKey = (typeof EMAIL_TEMPLATE_KEYS)[number];

export const EMAIL_ENTITY_TYPES = ['action', 'area', 'monitoring', 'contact'] as const;
export type EmailEntityType = (typeof EMAIL_ENTITY_TYPES)[number];
export const EMAIL_STATUSES = ['draft', 'sent', 'failed'] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];
export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = { draft: 'Entwurf', sent: 'gesendet', failed: 'fehlgeschlagen' };
export const EMAIL_PROVIDERS = ['graph', 'smtp', 'mailto'] as const;
export type EmailProvider = (typeof EMAIL_PROVIDERS)[number];
export const EMAIL_PROVIDER_LABELS: Record<EmailProvider, string> = { graph: 'Microsoft 365 (Kopie in Outlook)', smtp: 'Sammelpostfach (SMTP)', mailto: 'im E-Mail-Programm geöffnet' };
export const EMAIL_RECIPIENT_ROLES = ['forester', 'owner', 'partner'] as const;
export type EmailRecipientRole = (typeof EMAIL_RECIPIENT_ROLES)[number];
export const EMAIL_RECIPIENT_ROLE_LABELS: Record<EmailRecipientRole, string> = { forester: 'Förster', owner: 'Waldbesitzer', partner: 'Spendenpartner' };

/* ---------- Schemas ---------- */
export const EmailTriggerSchema = z.union([
  z.object({ after_item: z.string().min(1).max(40) }),
  z.object({ offset_days: z.number().int().min(-365).max(365) }),
  z.object({ monitoring_inquiry: z.literal(true) }),
]);
export type EmailTrigger = z.infer<typeof EmailTriggerSchema>;

/** Effekte beim Senden (6.4): Kästchen anhaken, Datumsfeld auf heute, Erinnerung in n Tagen */
export const ChecklistEffectsSchema = z.record(
  z.string().max(40),
  z.object({
    checks: z.object({ field: z.string().max(40), add: z.array(z.string().max(80)).min(1) }).optional(),
    set: z.record(z.string().max(40), z.string().max(80)).optional(),
    reminder_days: z.number().int().min(0).max(365).optional(),
  }),
);
export type ChecklistEffects = z.infer<typeof ChecklistEffectsSchema>;

export interface PlaceholderDef {
  key: string;
  description: string;
}

export const EmailTemplatePatchSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  subject: z.string().min(1).max(300).optional(),
  body: z.string().min(1).max(20000).optional(),
  trigger: EmailTriggerSchema.optional(),
  recipient_role: z.enum(EMAIL_RECIPIENT_ROLES).optional(),
  attachments: z.array(z.string().max(50)).max(10).nullable().optional(),
  checklist_effects: ChecklistEffectsSchema.nullable().optional(),
  action_types: z.array(z.string().max(20)).nullable().optional(),
  review_note: z.string().max(200).nullable().optional(),
  active: z.boolean().optional(),
  sort: z.number().int().optional(),
});
export type EmailTemplatePatch = z.infer<typeof EmailTemplatePatchSchema>;

export const DocumentTemplatePatchSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(400).nullable().optional(),
  output_kind: z.string().max(50).optional(),
  active: z.boolean().optional(),
});

const addr = z.string().max(300);
export const EmailPatchSchema = z.object({
  to: z.array(addr).max(20).optional(),
  cc: z.array(addr).max(20).nullable().optional(),
  bcc: z.array(addr).max(20).nullable().optional(),
  subject: z.string().min(1).max(500).optional(),
  body: z.string().max(50000).optional(),
  attachments: z.array(z.number().int()).max(20).nullable().optional(),
  updated_at: z.string().optional(),
});
export type EmailPatch = z.infer<typeof EmailPatchSchema>;

export const EmailFreeCreateSchema = z.object({
  entity_type: z.enum(EMAIL_ENTITY_TYPES),
  entity_id: z.number().int(),
  to: z.array(addr).max(20).default([]),
  subject: z.string().max(500).default(''),
  body: z.string().max(50000).default(''),
  attachments: z.array(z.number().int()).max(20).optional(),
});

/* ---------- DTOs ---------- */
export interface DocumentTemplateDto {
  id: number;
  key: DocumentTemplateKey;
  name: string;
  engine: 'docx' | 'html';
  document: { id: number; original_name: string; size_bytes: number | null; created_at: string } | null;
  version: number;
  placeholders: PlaceholderDef[];
  output_kind: string;
  description: string | null;
  active: boolean;
  updated_at: string;
}
export interface EmailTemplateDto {
  id: number;
  key: EmailTemplateKey | string;
  name: string;
  subject: string;
  body: string;
  trigger: EmailTrigger;
  trigger_label: string;
  recipient_role: EmailRecipientRole;
  attachments: string[];
  checklist_effects: ChecklistEffects | null;
  action_types: string[] | null;
  review_note: string | null;
  active: boolean;
  sort: number;
  updated_at: string;
}
export interface EmailDto {
  id: number;
  entity_type: EmailEntityType;
  entity_id: number;
  template_key: string | null;
  template_name: string | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  attachments: { id: number; original_name: string; size_bytes: number | null; mime: string | null; missing?: boolean }[];
  status: EmailStatus;
  status_label: string;
  due_date: string | null;
  sent_at: string | null;
  sent_by: { id: number; display_name: string; initials: string | null } | null;
  provider: EmailProvider | null;
  provider_label: string | null;
  error: string | null;
  /** Warnungen vor dem Senden (kein Empfänger, Anhang fehlt) */
  warnings: string[];
  created_at: string;
  updated_at: string;
}
/** Zustand einer Vorlage in der Akte (5.1 rechts) */
export type EmailSlotState = 'sent' | 'due' | 'upcoming' | 'waiting' | 'draft' | 'na';
export interface EmailSlotDto {
  template_key: string;
  name: string;
  state: EmailSlotState;
  state_label: string;
  due_date: string | null;
  email_id: number | null;
  sent_at: string | null;
  sent_by: string | null;
  recipient_role: EmailRecipientRole;
}
export interface EntityEmailsResponse {
  slots: EmailSlotDto[];
  history: EmailDto[];
  /** Hinweis, wie gesendet wird (Graph mit Consent, SMTP, nur mailto) */
  transport: MailTransportInfo;
}
export interface MailTransportInfo {
  graph_available: boolean;
  graph_consented: boolean;
  smtp_available: boolean;
  /** Entwicklungsmodus ohne echten Versand */
  smtp_dev: boolean;
  consent_url: string | null;
}
export interface GraphStatus {
  configured: boolean;
  consented: boolean;
  consented_at: string | null;
  consent_url: string | null;
}

/** Schlüsseldokumente der Aktion (5.1 links) */
export interface ActionDocumentRow {
  template_key: DocumentTemplateKey;
  name: string;
  state: 'present' | 'generated' | 'sent' | 'missing' | 'draft';
  hint: string;
  document: { id: number; original_name: string; created_at: string } | null;
  can_generate: boolean;
  generate_label: string;
  missing_values: string[];
  item_key: string | null;
}
export interface ActionDocumentsResponse {
  rows: ActionDocumentRow[];
  generated: { id: number; original_name: string; kind: string; created_at: string; created_by_name: string | null }[];
}
/** Vorschau eines Dokuments: Absätze mit Text und farbig markierten Werten */
export type PreviewPart = { text: string } | { key: string; value: string; missing: boolean; required: boolean };
export interface DocumentPreview {
  title: string;
  engine: 'docx' | 'html';
  paragraphs: { heading?: string; parts: PreviewPart[]; muted?: boolean }[];
  missing: string[];
  /** unbekannte Platzhalter in der hochgeladenen Word-Vorlage */
  unknown: string[];
  can_generate: boolean;
  generate_label: string;
  version: number;
  has_file: boolean;
}

/* ---------- Platzhalter-Kontext (6.1) ---------- */
export type TemplateContext = Record<string, string>;

export const PLACEHOLDERS: PlaceholderDef[] = [
  { key: 'action.code', description: 'Kennung der Aktion, z. B. PFM261103_Beiersdorf_Töpsheide' },
  { key: 'action.date_de', description: 'Aktionstag als TT.MM.JJJJ' },
  { key: 'action.time', description: 'Uhrzeit von–bis' },
  { key: 'action.partner_name', description: 'Spendenpartner' },
  { key: 'action.trees_planned_de', description: 'Bäume geplant, mit Tausenderpunkt' },
  { key: 'action.species_text', description: 'Baumarten mit Anzahl' },
  { key: 'action.participants', description: 'Teilnehmende geplant' },
  { key: 'action.price_per_tree_de', description: 'Baumpreis brutto je Baum, z. B. 4,60' },
  { key: 'action.season', description: 'Saison, z. B. Herbst 2026' },
  { key: 'action.city', description: 'Ort der Aktion' },
  { key: 'area.name', description: 'Name der Fläche' },
  { key: 'area.municipality', description: 'Gemeinde' },
  { key: 'area.district', description: 'Gemarkung' },
  { key: 'area.flur', description: 'Flur / Flurstück' },
  { key: 'area.parcel', description: 'Flurstück' },
  { key: 'area.compartment', description: 'Abteilung' },
  { key: 'area.hectares_de', description: 'Hektar, z. B. 0,8' },
  { key: 'area.coords', description: 'Koordinaten Dezimalgrad' },
  { key: 'area.owner_name', description: 'Waldbesitzer (Kontakt oder Freitext)' },
  { key: 'area.state', description: 'Bundesland' },
  { key: 'area.region', description: 'Region' },
  { key: 'visit.date_de', description: 'Begehungsdatum' },
  { key: 'visit.helpers_count', description: 'Pflanzhelfer des Försters' },
  { key: 'visit.preparation', description: 'Flächenvorbereitung' },
  { key: 'visit.planting_holes', description: 'Pflanzlochvorbereitung' },
  { key: 'visit.wildlife_protection', description: 'Wildschutz laut Protokoll' },
  { key: 'visit.remaining_planting_by', description: 'Restpflanzung durch' },
  { key: 'visit.parking_coords', description: 'Koordinaten Parken' },
  { key: 'visit.street_name', description: 'Straße zur Waldeinfahrt' },
  { key: 'forester.salutation_line', description: 'Anrede, z. B. Sehr geehrter Herr Brandt' },
  { key: 'forester.name', description: 'Name des Försters' },
  { key: 'forester.org', description: 'Forstamt / Organisation' },
  { key: 'forester.street', description: 'Straße' },
  { key: 'forester.zip_city', description: 'PLZ und Ort' },
  { key: 'forester.phone', description: 'Telefon oder Mobil' },
  { key: 'forester.email', description: 'E-Mail' },
  { key: 'owner.name', description: 'Waldbesitzer' },
  { key: 'owner.salutation_line', description: 'Anrede Waldbesitzer' },
  { key: 'owner.address', description: 'Adresse Waldbesitzer' },
  { key: 'user.name', description: 'Angemeldete Person' },
  { key: 'user.role_title', description: 'Rolle der angemeldeten Person' },
  { key: 'user.signature', description: 'Signatur aus dem Profil' },
  { key: 'user.email', description: 'E-Mail der angemeldeten Person' },
  { key: 'tww.name', description: 'Trinkwasserwald e.V.' },
  { key: 'tww.address', description: 'Anschrift des Vereins' },
  { key: 'tww.board', description: 'Vorstand laut Einstellungen' },
  { key: 'tww.invoice_email', description: 'Rechnungsadresse' },
  { key: 'today_de', description: 'heutiges Datum' },
];
export const PLACEHOLDER_KEYS = new Set(PLACEHOLDERS.map((p) => p.key));

/** Deutsche Zahl mit fester Nachkommazahl (4.6 → "4,60"); ohne Wert leer */
export const fmtDe = (n: number | string | null | undefined, digits = 2): string => (n == null || n === '' ? '' : Number(n).toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits }));
export const fmtIntDe = (n: number | string | null | undefined): string => (n == null || n === '' ? '' : Number(n).toLocaleString('de-DE', { maximumFractionDigits: 0 }));
export const fmtHaDe = (n: number | string | null | undefined): string => (n == null || n === '' ? '' : String(Math.round(Number(n) * 100) / 100).replace('.', ','));
const dateDe = (iso: string | null | undefined) => (iso ? fmtDate(iso) : '');

/** Anrede aus Kontaktdaten: „Sehr geehrte Frau Petersen“, „Sehr geehrter Herr Dr. Brandt“, sonst „Guten Tag Vorname Nachname“ */
export function salutationLine(c: { salutation?: string | null; title?: string | null; first_name?: string | null; last_name?: string | null; display_name?: string | null } | null | undefined): string {
  if (!c) return 'Sehr geehrte Damen und Herren';
  const name = [c.title, c.last_name].filter(Boolean).join(' ').trim();
  if (c.salutation === 'Frau' && name) return `Sehr geehrte Frau ${name}`;
  if (c.salutation === 'Herr' && name) return `Sehr geehrter Herr ${name}`;
  const full = [c.title, c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.display_name;
  return full ? `Guten Tag ${full}` : 'Sehr geehrte Damen und Herren';
}

export interface ContextInput {
  action?: { code: string; date: string | null; time_start?: string | null; time_end?: string | null; partner_name: string | null; trees_planned: number | null; species_text: string | null; participants_planned: number | null; price_per_tree: number | string | null; season: string; city: string | null } | null;
  area?: { name: string; municipality: string | null; district: string | null; flur: string | null; parcel: string | null; compartment: string | null; hectares: number | string | null; lat: number | string | null; lng: number | string | null; owner_name: string | null; state: string | null; region: string | null } | null;
  visit?: { visited_at: string | null; helpers_count: number | null; preparation: string | null; planting_holes: string | null; wildlife_protection: string | null; remaining_planting_by: string | null; parking_lat: number | string | null; parking_lng: number | string | null; street_name: string | null; price_per_tree_max: number | string | null; owner_name_text?: string | null } | null;
  forester?: { salutation: string | null; title: string | null; first_name: string | null; last_name: string | null; display_name: string; org: string | null; street: string | null; zip: string | null; city: string | null; phone: string | null; mobile: string | null; email: string | null } | null;
  owner?: { salutation: string | null; title: string | null; first_name: string | null; last_name: string | null; display_name: string; street: string | null; zip: string | null; city: string | null; email: string | null } | null;
  user?: { display_name: string; email: string; role_title: string; signature: string | null } | null;
  tww: { name: string; address: string; board: string; invoice_email: string };
  today: string;
}

const coords = (lat: number | string | null | undefined, lng: number | string | null | undefined) => (lat != null && lng != null && lat !== '' && lng !== '' ? `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}` : '');

/** 6.1 Kontext-Builder: flaches Objekt mit Namensräumen, deutsche Formatierung, fehlende Werte leer */
export function buildTemplateContext(i: ContextInput): TemplateContext {
  const a = i.action;
  const ar = i.area;
  const v = i.visit;
  const f = i.forester;
  const o = i.owner;
  const u = i.user;
  const price = a?.price_per_tree ?? v?.price_per_tree_max ?? null;
  const ctx: TemplateContext = {
    'action.code': a?.code ?? '',
    'action.date_de': dateDe(a?.date),
    'action.time': [a?.time_start, a?.time_end].filter(Boolean).join(' bis '),
    'action.partner_name': a?.partner_name ?? '',
    'action.trees_planned_de': fmtIntDe(a?.trees_planned),
    'action.species_text': a?.species_text ?? '',
    'action.participants': a?.participants_planned != null ? String(a.participants_planned) : '',
    'action.price_per_tree_de': fmtDe(price),
    'action.season': a?.season ?? '',
    'action.city': a?.city ?? '',
    'area.name': ar?.name ?? '',
    'area.municipality': ar?.municipality ?? '',
    'area.district': ar?.district ?? '',
    'area.flur': [ar?.flur, ar?.parcel].filter(Boolean).join(' / '),
    'area.parcel': ar?.parcel ?? '',
    'area.compartment': ar?.compartment ?? '',
    'area.hectares_de': fmtHaDe(ar?.hectares),
    'area.coords': coords(ar?.lat, ar?.lng),
    'area.owner_name': o?.display_name ?? ar?.owner_name ?? v?.owner_name_text ?? '',
    'area.state': ar?.state ?? '',
    'area.region': ar?.region ?? '',
    'visit.date_de': dateDe(v?.visited_at),
    'visit.helpers_count': v?.helpers_count != null ? String(v.helpers_count) : '',
    'visit.preparation': v?.preparation ?? '',
    'visit.planting_holes': v?.planting_holes ?? '',
    'visit.wildlife_protection': v?.wildlife_protection ?? '',
    'visit.remaining_planting_by': v?.remaining_planting_by ?? '',
    'visit.parking_coords': coords(v?.parking_lat, v?.parking_lng),
    'visit.street_name': v?.street_name ?? '',
    'forester.salutation_line': salutationLine(f),
    'forester.name': f?.display_name ?? '',
    'forester.org': f?.org ?? '',
    'forester.street': f?.street ?? '',
    'forester.zip_city': [f?.zip, f?.city].filter(Boolean).join(' '),
    'forester.phone': f?.mobile ?? f?.phone ?? '',
    'forester.email': f?.email ?? '',
    'owner.name': o?.display_name ?? ar?.owner_name ?? v?.owner_name_text ?? '',
    'owner.salutation_line': salutationLine(o),
    'owner.address': o ? [o.street, [o.zip, o.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '',
    'user.name': u?.display_name ?? '',
    'user.role_title': u?.role_title ?? '',
    'user.signature': u?.signature?.trim() || [u?.display_name, u?.role_title ? `${u.role_title}, ${i.tww.name}` : i.tww.name].filter(Boolean).join('\n'),
    'user.email': u?.email ?? '',
    'tww.name': i.tww.name,
    'tww.address': i.tww.address,
    'tww.board': i.tww.board,
    'tww.invoice_email': i.tww.invoice_email,
    today_de: dateDe(i.today),
  };
  return ctx;
}

/* ---------- Vorlagen rendern ---------- */
const TAG = /\{([a-z_][a-z0-9_.]*)\}/g;

/** Platzhalter in Text ersetzen; unbekannte bleiben stehen, leere werden leer */
export function renderTemplate(text: string, ctx: TemplateContext): string {
  return text.replace(TAG, (m, key: string) => (key in ctx ? ctx[key]! : m));
}
/** Platzhalter im Text, die im Kontext fehlen oder leer sind */
export function missingPlaceholders(text: string, ctx: TemplateContext): string[] {
  const out = new Set<string>();
  for (const m of text.matchAll(TAG)) if (!ctx[m[1]!]) out.add(m[1]!);
  return [...out];
}
export function unknownPlaceholders(text: string): string[] {
  const out = new Set<string>();
  for (const m of text.matchAll(TAG)) if (!PLACEHOLDER_KEYS.has(m[1]!)) out.add(m[1]!);
  return [...out];
}
export function placeholdersIn(text: string): string[] {
  return [...new Set([...text.matchAll(TAG)].map((m) => m[1]!))];
}
/** Text in Teile zerlegen (Vorschau mit grün/rot hinterlegten Werten) */
export function splitTemplate(text: string, ctx: TemplateContext, required: Set<string> = new Set()): PreviewPart[] {
  const parts: PreviewPart[] = [];
  let last = 0;
  for (const m of text.matchAll(TAG)) {
    if (m.index! > last) parts.push({ text: text.slice(last, m.index) });
    const key = m[1]!;
    const value = ctx[key] ?? '';
    parts.push({ key, value, missing: !value, required: required.has(key) });
    last = m.index! + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/* ---------- Waldbesitzervertrag (6.2) ---------- */
/** Pflichtwerte laut Prototyp: Waldbesitzer, Gemeinde, Gemarkung, Flur/Flurstück, Abteilung, Hektar, Förster mit Organisation, Adresse, Telefon, Mail, Begehungsdatum, Preis, Kennung */
export const CONTRACT_REQUIRED = ['area.owner_name', 'area.municipality', 'area.district', 'area.flur', 'area.compartment', 'area.hectares_de', 'forester.name', 'forester.org', 'forester.street', 'forester.zip_city', 'forester.phone', 'forester.email', 'visit.date_de', 'action.price_per_tree_de', 'action.code'] as const;
export const CONTRACT_REQUIRED_LABELS: Record<string, string> = {
  'area.owner_name': 'Waldbesitzer',
  'area.municipality': 'Gemeinde',
  'area.district': 'Gemarkung',
  'area.flur': 'Flur / Flurstück',
  'area.compartment': 'Abteilung',
  'area.hectares_de': 'Hektar',
  'forester.name': 'Förster',
  'forester.org': 'Organisation des Försters',
  'forester.street': 'Straße des Försters',
  'forester.zip_city': 'PLZ und Ort des Försters',
  'forester.phone': 'Telefon des Försters',
  'forester.email': 'E-Mail des Försters',
  'visit.date_de': 'Begehungsdatum',
  'action.price_per_tree_de': 'Preis je Baum',
  'action.code': 'Kennung',
};
/** Vertragstext aus dem Prototyp als HTML-Vorlage (Vorschau und Fallback, wenn keine Word-Vorlage hochgeladen ist) */
export const CONTRACT_PARAGRAPHS: { heading?: string; text: string; muted?: boolean }[] = [
  { text: 'Vertrag zur Förderung des naturnahen Waldumbaus zwischen {tww.name}, {tww.address}, vertreten durch {tww.board}, Vorstand, und {area.owner_name}, Grundeigentümer.' },
  { heading: '§ 1 Vertragsfläche', text: 'Vertragsgegenstand ist die Waldfläche in der Gemeinde {area.municipality}, Gemarkung {area.district}, Flur/Flurstück {area.flur}, Abteilung {area.compartment}. Die zu bepflanzende Fläche umfasst {area.hectares_de} ha.' },
  { text: 'Ansprechpartner in Sachen Waldumbau ist {forester.name}, {forester.org}, {forester.street}, {forester.zip_city}, {forester.phone}, {forester.email}.' },
  { heading: '§ 2 Begehungsprotokoll', text: 'Dieser Vertrag stützt sich auf das Protokoll der gemeinsamen Begehung vom {visit.date_de}. Das Protokoll ist als Anlage beigefügt.' },
  { heading: '§ 3 Durchführung der Maßnahme und Finanzierung', text: 'Die Kosten zur Kulturbegründung werden bis zu maximal {action.price_per_tree_de} € brutto je Baum durch TWW aus Spenden und Fördermitteln übernommen. Rechnungen sind unter Angabe von {action.code} an {tww.invoice_email} zu senden.' },
  { heading: '§ 6 bis § 10', text: 'Waldbauliches Ziel, Monitoring über 10 Jahre, Laufzeit und Schlussbestimmungen unverändert aus der Vorlage.', muted: true },
];
export const PLANTING_CONFIRMATION_REQUIRED = ['action.partner_name', 'action.code', 'forester.name', 'area.name', 'action.date_de', 'action.trees_planned_de'] as const;
export const PLANTING_CONFIRMATION_PARAGRAPHS: { heading?: string; text: string; muted?: boolean }[] = [
  { heading: 'Pflanzbestätigung', text: 'für {action.partner_name} · Aktion {action.code}' },
  { text: 'Hiermit bestätigt {forester.name}, {forester.org}, dass auf der Fläche {area.name} ({area.coords}) im Rahmen der Pflanzaktion am {action.date_de} insgesamt {action.trees_planned_de} Bäume gepflanzt wurden.' },
  { text: 'Baumarten: {action.species_text}\nWildschutz: {visit.wildlife_protection}\nRestpflanzung durch: {visit.remaining_planting_by}' },
  { text: 'Ort, Datum, Unterschrift forstlicher Betreuer', muted: true },
];

/* ---------- E-Mail-Fälligkeit (6.4) ---------- */
export function triggerLabel(t: EmailTrigger): string {
  if ('after_item' in t) return `nach Punkt „${t.after_item}“`;
  if ('monitoring_inquiry' in t) return 'Monitoring-Nachfrage (1. Mai des Fälligkeitsjahrs)';
  const d = t.offset_days;
  if (d === 0) return 'am Aktionstag';
  return d < 0 ? `${-d} Tage vor dem Aktionstag` : `${d} Tage nach dem Aktionstag`;
}
/**
 * Fälligkeit einer Vorlage für eine Aktion: nach Punkt X erledigt → sofort (Tag der Erledigung), Versatz → Aktionstag ± n;
 * null, wenn (noch) nicht bestimmbar (Punkt offen, kein Aktionstag).
 */
export function emailDueDate(trigger: EmailTrigger, action: { date: string | null; items: { key: string; state: string; completed_at: string | null }[] }, today: string): string | null {
  if ('after_item' in trigger) {
    const it = action.items.find((i) => i.key === trigger.after_item);
    if (!it || it.state !== 'done') return null;
    const done = it.completed_at ? it.completed_at.slice(0, 10) : today;
    return done < today ? done : today;
  }
  if ('offset_days' in trigger) return action.date ? addDays(action.date, trigger.offset_days) : null;
  return null;
}
/** Fällig heißt: nicht gesendet und Fälligkeit innerhalb der nächsten 14 Tage oder vergangen */
export function isEmailDue(due: string | null, today: string, horizonDays = 14): boolean {
  return !!due && due <= addDays(today, horizonDays);
}
export function emailTemplateApplies(t: { action_types: string[] | null; active: boolean }, actionType: string | null): boolean {
  if (!t.active) return false;
  if (!t.action_types || !t.action_types.length) return true;
  return !!actionType && t.action_types.includes(actionType);
}
export function slotState(p: { sent: boolean; draft: boolean; due: string | null; today: string; applies: boolean }): { state: EmailSlotState; label: string } {
  if (!p.applies) return { state: 'na', label: 'gilt nicht für diesen Aktionstyp' };
  if (p.sent) return { state: 'sent', label: 'gesendet' };
  if (p.draft) return { state: 'draft', label: 'Entwurf gespeichert' };
  if (!p.due) return { state: 'waiting', label: 'nach der Begehung' };
  if (p.due <= p.today) return { state: 'due', label: 'jetzt fällig' };
  if (isEmailDue(p.due, p.today)) return { state: 'due', label: `fällig ${fmtDate(p.due)}` };
  return { state: 'upcoming', label: `fällig ${fmtDate(p.due)}` };
}

/** Adresse „Name <mail>“ zerlegen */
export function parseAddress(s: string): { name: string | null; email: string } | null {
  const m = /^\s*(?:"?([^"<]*?)"?\s*)?<([^<>\s]+@[^<>\s]+)>\s*$/.exec(s) ?? /^\s*([^<>\s]+@[^<>\s]+)\s*$/.exec(s);
  if (!m) return null;
  return m.length === 3 ? { name: m[1]?.trim() || null, email: m[2]!.trim() } : { name: null, email: m[1]!.trim() };
}
export const formatAddress = (name: string | null | undefined, email: string) => (name ? `${name} <${email}>` : email);

/* ---------- Startfassung der sechs Vorlagen (Prototyp MAILS) ---------- */
export interface EmailTemplateSeed {
  key: EmailTemplateKey;
  name: string;
  subject: string;
  body: string;
  trigger: EmailTrigger;
  recipient_role: EmailRecipientRole;
  attachments: string[];
  checklist_effects: ChecklistEffects | null;
  sort: number;
}
export const DEFAULT_EMAIL_TEMPLATES: EmailTemplateSeed[] = [
  {
    key: 'after_site_visit',
    name: 'Nach der Begehung: Protokoll, Vertrag und Anfahrtsskizze',
    subject: 'Begehung {area.name}: Protokoll, Waldbesitzervertrag und Anfahrtsskizze',
    body: 'vielen Dank für die gemeinsame Begehung am {visit.date_de}. Anbei senden wir Ihnen:\n- das Begehungsprotokoll\n- den Waldbesitzervertrag zur Unterschrift\n- die Anfahrtsskizze\n\nWie besprochen planen wir die Pflanzaktion mit {action.partner_name}:\n- Termin: **{action.date_de}**\n- rund {action.participants} Teilnehmende\n- {action.trees_planned_de} Bäume\n- wir übernehmen bis zu {action.price_per_tree_de} € brutto je Baum\n\n**Bitte senden Sie uns den gegengezeichneten Vertrag postalisch** an {tww.board}, {tww.name}, {tww.address}.',
    trigger: { after_item: 'besichtigung' },
    recipient_role: 'forester',
    attachments: ['site_visit', 'contract_owner', 'anfahrtsskizze'],
    checklist_effects: { vertrag: { set: { versendet: 'today' } }, infomaterial: { checks: { field: 'an', add: ['Förster'] } } },
    sort: 10,
  },
  {
    key: 'four_weeks_before',
    name: '4 Wochen vorher: Ablaufplan und Abfrage',
    subject: 'Pflanzaktion am {action.date_de}: Ablaufplan und Abstimmung',
    body: 'in vier Wochen, am {action.date_de}, findet unsere Pflanzaktion mit {action.partner_name} auf der Fläche {area.name} statt. Anbei der Ablaufplan.\n\nBitte geben Sie uns kurz Rückmeldung zu folgenden Punkten:\n- Zustand der Fläche und Stand der Vorbereitung ({visit.preparation}, {visit.planting_holes})\n- Einsatz Ihrer Forstwirte ({visit.helpers_count} Personen)\n- Toilettenwagen wird am Vortag angeliefert, Stellplatz wie besprochen\n- Treffpunkt und Uhrzeit vor Ort',
    trigger: { offset_days: -28 },
    recipient_role: 'forester',
    attachments: ['ablaufplan'],
    checklist_effects: { karte_ablauf: { checks: { field: 'an', add: ['Förster'] } } },
    sort: 20,
  },
  {
    key: 'one_week_before',
    name: '1 Woche vorher: letzte Abstimmung',
    subject: 'Pflanzaktion am {action.date_de}: letzte Abstimmung',
    body: 'nächste Woche ist es so weit. Wir kommen am **{action.date_de} um 9:00 Uhr** mit dem Transporter an den Sammelplatz ({visit.parking_coords}). Die Bäume sind laut Baumschule geliefert.\n\nGibt es von Ihrer Seite noch etwas zu beachten, zum Beispiel:\n- Wetter\n- Zuwegung\n- Sperrungen',
    trigger: { offset_days: -7 },
    recipient_role: 'forester',
    attachments: [],
    checklist_effects: { infomaterial: { checks: { field: 'an', add: ['Förster'] } } },
    sort: 30,
  },
  {
    key: 'thanks_after',
    name: 'Nach der Aktion: Dank und Fotos',
    subject: 'Vielen Dank für die Pflanzaktion am {action.date_de}',
    body: 'herzlichen Dank für die gelungene Pflanzaktion am {action.date_de}. {action.participants} Teilnehmende von {action.partner_name} haben mit Ihrer Unterstützung {action.trees_planned_de} Bäume auf {area.name} gepflanzt. Anbei einige Fotos vom Tag.\n\nFür unsere Unterlagen benötigen wir nach Abschluss der Restpflanzung noch die Pflanzbestätigung. Wir melden uns dazu gesondert.',
    trigger: { offset_days: 7 },
    recipient_role: 'forester',
    attachments: ['foto'],
    checklist_effects: { infomaterial: { checks: { field: 'an', add: ['Förster'] } } },
    sort: 40,
  },
  {
    key: 'request_confirmation',
    name: 'Pflanzbestätigung anfordern',
    subject: 'Pflanzbestätigung für {action.code}',
    body: 'für unsere Nachweise gegenüber {action.partner_name} benötigen wir die Pflanzbestätigung für die Fläche {area.name}. Anbei die vorbefüllte Vorlage mit {action.trees_planned_de} Bäumen und den vereinbarten Baumarten. Bitte:\n1. die Angaben prüfen,\n2. fehlende Angaben ergänzen,\n3. die Bestätigung unterschrieben an uns zurücksenden.',
    trigger: { offset_days: 7 },
    recipient_role: 'forester',
    attachments: ['planting_confirmation'],
    checklist_effects: { pflanzbestaetigung: { reminder_days: 14 } },
    sort: 50,
  },
  {
    key: 'monitoring_inquiry',
    name: 'Monitoring-Nachfrage beim Förster',
    subject: 'Nachfrage Kulturzustand {area.name}',
    body: 'für unsere jährliche Nachfrage bitten wir um eine kurze Rückmeldung zur Fläche „{area.name}“:\n\n- Ist der Wildschutz intakt?\n- Wie ist der Anwuchserfolg, gibt es nennenswerte Ausfälle (ggf. je Baumart)?\n- Ist eine Nachbesserung erforderlich?\n- Gibt es sonstige Vereinbarungen oder Hinweise?\n\nGern auch mit ein bis zwei Fotos.',
    trigger: { monitoring_inquiry: true },
    recipient_role: 'forester',
    attachments: [],
    checklist_effects: null,
    sort: 60,
  },
];

/** Vollständiger Mailtext: Anrede, Text, Grußformel und Signatur */
export function composeBody(salutation: string, body: string, signature: string): string {
  return `${salutation},\n\n${body.trim()}\n\nMit freundlichen Grüßen\n${signature.trim()}`;
}
